/**
 * `createEngine: CreateEngine` (W1-05b engine-graph; contract core/engine/types.ts, ARCHITECTURE.md §7.1, §7.3,
 * rendering.md R§8, R§10).
 *
 * The incremental evaluation engine over a document source (the main-thread DocStore or a worker replica):
 *  - graph.ts   the dirty DAG: lazy per-line lowering cache (W1-04 Lower), W1-05a evaluation per line, tempo,
 *               bezier registry, max_time, floors, topology, the compile context handed to the seams;
 *  - query.ts   channel readouts and samplers, floor and effective-speed sampling, segmentAt, pickNote, stats;
 *  - frame.ts   FrameDrawList pooling and the line bodies;
 *  - verify.ts  the dev verify mode (full recompile + diff).
 * Everything about notes (seconds, multi-highlight, appear time, judged-play candidates, emissions, note quads,
 * ghosts, hit effects, paint stamps, HUD) is delegated to the notes seam (W1-06, `notesModule`), effects and videos
 * to the effects seam (W1-08, `effectsModule`). `frame()` calls the frame-filling seam methods in Phira's
 * composition order (R§8): videos, line bodies, notes, non-global and global effects, hit effects and particles,
 * HUD; the editor-only ghost outlines come last.
 *
 * Laziness: ChangeSets only mark nodes stale and bump `revision`; the first query after them (or `sync()`)
 * recompiles. Lowering options are re-derived from `doc.pkg` on every pkg-dirty ChangeSet; `opts.lower.overrides`
 * only serve explicit "what if" previews. With `verifyEvery: N > 0` every N-th sync that did work recompiles
 * from scratch and throws an AssertionError listing the differences (dev builds); verification includes the frame
 * output (note quads, ghosts, hit effects, HUD, line bodies) in the window of the last frame drawn.
 *
 * Aspect ratio: a line's y (and everything rotated with it) depends on the viewport aspect. `frame()` uses its
 * viewport's; `lineStates` / `lineState` use the chart aspect unless the optional trailing `ar` is given (contract,
 * CR-W1-05a-1); `paintStamps` follows the last frame drawn; `noteAppearSec` the window it is given.
 */

import { effectsModule } from './effects/index';
import { createFrameList, fillFrameContent, reuseFrameList, type FrameSources } from './frame';
import { EngineGraph, nowMs, type EngineSeams } from './graph';
import { notesModule } from './notes/index';
import {
  channelValue,
  engineStats,
  floorPosition,
  pickNote,
  sampleChannel,
  sampleEffectiveSpeed,
  sampleFloor,
  segmentAt,
} from './query';
import type {
  CreateEngine,
  Emission,
  Engine,
  EngineOptions,
  EngineSource,
  EngineStats,
  FrameDrawList,
  FrameOptions,
  HudValues,
  LineStateRt,
  PaintStampBatch,
  PhiraPlayerSettings,
  PreviewFilters,
  SkinMetrics,
  Viewport,
} from './types';
import { verifyGraph, VERIFY_LIMIT } from './verify';
import { computeViewport } from './viewport';
import type { IrChart, IrExtraJson, IrLine, LowerReport } from '../ir/types';
import type { AssetId, KeyId, LineId, LineProp, NoteId, PackageSettings, TrackId } from '../model/types';
import type { Sec, TempoMap } from '../time/types';
import { AssertionError } from '../util/assert';

/** Engine members beyond the contract (additive; see the module comment). */
export interface EngineExtensions {
  /** The graph behind the engine (diagnostics and tests). */
  readonly graph: EngineGraph;
}

export type EngineImpl = Engine & EngineExtensions;

const VERIFY_REPORT_LINES = 20;

class Viewports {
  private key = '';
  private vp: Viewport | null = null;

  get(winW: number, winH: number, pkg: PackageSettings, aspectOverride: number | null): Viewport {
    const key = `${winW}|${winH}|${pkg.aspectRatio}|${String(pkg.forceAspectRatio)}|${String(aspectOverride)}`;
    if (this.vp === null || key !== this.key) {
      this.vp = computeViewport(winW, winH, pkg, aspectOverride);
      this.key = key;
    }
    return this.vp;
  }
}

class EngineObject implements EngineImpl {
  readonly graph: EngineGraph;
  private readonly seams: EngineSeams;
  private readonly viewports = new Viewports();
  private lastFrameMs = 0;
  /** Options of the last frame drawn: verify mode compares frame output in that window (null = defaults). */
  private lastFrameOpts: FrameOptions | null = null;
  private background: { readonly illustration: AssetId | null; readonly dim: number } = {
    illustration: null,
    dim: 0,
  };

  constructor(src: EngineSource, opts: EngineOptions, seams: EngineSeams) {
    this.seams = seams;
    this.graph = new EngineGraph(src, opts, seams);
    const every = opts.verifyEvery ?? 0;
    if (every > 0) {
      this.graph.setSyncListener(() => {
        if (this.graph.syncCount % every !== 0) return;
        const diffs = verifyGraph(this.graph, VERIFY_LIMIT, this.lastFrameOpts ?? undefined);
        if (diffs.length > 0) {
          throw new AssertionError(
            `engine verify: the incremental state differs from a full recompile (${diffs.length} difference(s)):\n` +
              diffs.slice(0, VERIFY_REPORT_LINES).join('\n'),
          );
        }
      });
    }
  }

  /** Synced graph. */
  private g(): EngineGraph {
    const g = this.graph;
    if (g.isDisposed) throw new AssertionError('engine: used after dispose()');
    g.sync();
    return g;
  }

  get revision(): number {
    return this.graph.revision;
  }

  sync(): void {
    this.g();
  }

  get tempo(): TempoMap {
    return this.g().tempo;
  }

  get maxTime(): Sec {
    return this.g().maxTime;
  }

  // ------------------------------------------------------------------------------------------------------------
  // Lowered IR
  // ------------------------------------------------------------------------------------------------------------

  ir(): IrChart {
    return this.g().chart();
  }

  irExtra(): IrExtraJson | null {
    return this.g().extra;
  }

  irLine(line: LineId): IrLine {
    return this.g().irLine(line);
  }

  lowerReport(): LowerReport {
    return this.g().report();
  }

  // ------------------------------------------------------------------------------------------------------------
  // Line states and queries
  // ------------------------------------------------------------------------------------------------------------

  lineStates(t: Sec, filters?: PreviewFilters, ar?: number): readonly LineStateRt[] {
    const g = this.g();
    return g.main.evalAll(t, ar ?? g.chartAr(), filters, g.syncCount);
  }

  lineState(line: LineId, t: Sec, filters?: PreviewFilters, ar?: number): LineStateRt {
    const g = this.g();
    const node = g.node(line);
    if (node === undefined) throw new RangeError(`engine: line ${String(line)} is not in the chart`);
    return g.main.evalLine(node.index, t, ar ?? g.chartAr(), filters);
  }

  channelValue(line: LineId, prop: LineProp, t: Sec, layer?: number | 'sum'): number {
    return channelValue(this.g(), line, prop, t, layer);
  }

  sampleChannel(line: LineId, prop: LineProp, layer: number | 'sum', t0: Sec, t1: Sec, out: Float32Array): void {
    sampleChannel(this.g(), line, prop, layer, t0, t1, out);
  }

  floorPosition(line: LineId, t: Sec): number {
    return floorPosition(this.g(), line, t);
  }

  sampleFloor(line: LineId, t0: Sec, t1: Sec, out: Float32Array): void {
    sampleFloor(this.g(), line, t0, t1, out);
  }

  sampleEffectiveSpeed(line: LineId, t0: Sec, t1: Sec, out: Float32Array): void {
    sampleEffectiveSpeed(this.g(), line, t0, t1, out);
  }

  segmentAt(track: TrackId, t: Sec): KeyId | null {
    return segmentAt(this.g(), track, t);
  }

  // ------------------------------------------------------------------------------------------------------------
  // Notes (delegated to the notes seam)
  // ------------------------------------------------------------------------------------------------------------

  noteSec(note: NoteId): Sec {
    const g = this.g();
    return this.seams.notes.noteSec(g.ctx, note);
  }

  noteEndSec(note: NoteId): Sec {
    const g = this.g();
    return this.seams.notes.noteEndSec(g.ctx, note);
  }

  isMultiHighlight(note: NoteId): boolean {
    const g = this.g();
    return this.seams.notes.isMultiHighlight(g.ctx, note);
  }

  noteAppearSec(
    note: NoteId,
    player: PhiraPlayerSettings,
    skin: SkinMetrics,
    winW: number,
    winH: number,
    atLeast?: number,
  ): Sec | null {
    const g = this.g();
    const vp = this.viewports.get(winW, winH, g.src.doc.pkg, player.aspectOverride);
    return g.withView(vp.ar, undefined, () =>
      this.seams.notes.noteAppearSec(g.ctx, note, player, skin, winW, winH, atLeast),
    );
  }

  notesInWindow(t0: Sec, t1: Sec): readonly NoteId[] {
    const g = this.g();
    return this.seams.notes.notesInWindow(g.ctx, t0, t1);
  }

  pickNote(list: FrameDrawList, x: number, y: number): NoteId | null {
    return pickNote(list, x, y);
  }

  emissions(t0: Sec, t1: Sec, rate: number, filters?: PreviewFilters): readonly Emission[] {
    const g = this.g();
    const out: Emission[] = [];
    this.seams.notes.emissions(g.ctx, t0, t1, rate, filters, out);
    return out;
  }

  paintStamps(line: LineId, t: Sec, winW: number): PaintStampBatch {
    const g = this.g();
    const ar = g.lastFrameAr ?? g.chartAr();
    return g.withView(ar, undefined, () => this.seams.notes.paintStamps(g.ctx, line, t, winW));
  }

  hud(t: Sec): HudValues {
    const g = this.g();
    return this.seams.notes.hudValues(g.ctx, t);
  }

  // ------------------------------------------------------------------------------------------------------------
  // Frame
  // ------------------------------------------------------------------------------------------------------------

  frame(t: Sec, opts: FrameOptions, out?: FrameDrawList): FrameDrawList {
    const t0 = nowMs();
    const g = this.g();
    const doc = g.src.doc;
    const pkg = doc.pkg;
    const b = out === undefined ? createFrameList() : reuseFrameList(out);
    const vp = this.viewports.get(opts.winW, opts.winH, pkg, opts.player.aspectOverride);
    b.t = t;
    b.viewport = vp;
    b.resAlpha = opts.resAlpha;
    const illustration = doc.meta.illustration;
    // Preview-only player emulation of Phira's background dim (CR-W2-02-1); else the chart's own value.
    const dim = opts.backgroundDimOverride ?? pkg.backgroundDim;
    if (this.background.illustration !== illustration || !Object.is(this.background.dim, dim)) {
      this.background = { illustration, dim };
    }
    b.background = this.background;
    g.lastFrameAr = vp.ar;
    const states = g.main.evalAll(t, vp.ar, opts.filters, g.syncCount);
    const sources: FrameSources = {
      notes: this.seams.notes,
      effects: this.seams.effects,
      ctx: g.ctx,
      states,
      drawOrder: g.drawOrder(),
      drawInfo: (i) => g.drawInfoAt(i),
      pkg,
    };
    // R§8 order (videos, line bodies, notes, effects, hit effects and particles, HUD; ghost outlines last), with
    // ctx.lineState evaluating at the frame's aspect and filters.
    g.withView(vp.ar, opts.filters, () => {
      fillFrameContent(b, sources, t, opts, vp);
    });
    this.lastFrameMs = nowMs() - t0;
    this.lastFrameOpts = opts;
    return b;
  }

  stats(): EngineStats {
    return engineStats(this.g(), this.lastFrameMs);
  }

  verify(): readonly string[] {
    this.g();
    return verifyGraph(this.graph, VERIFY_LIMIT, this.lastFrameOpts ?? undefined);
  }

  dispose(): void {
    this.graph.dispose();
  }
}

/** The engine with explicit seams (tests use recording fakes; `createEngine` passes the real modules). */
export function createEngineWith(src: EngineSource, opts: EngineOptions, seams: EngineSeams): EngineImpl {
  return new EngineObject(src, opts, seams);
}

export const createEngine: CreateEngine = (src, opts) =>
  createEngineWith(src, opts, { notes: notesModule, effects: effectsModule });
